import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { filter, map, startWith } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { environment } from '../../../../assist/src/environments/environment';
import { authConfig } from './auth-config';

@Injectable({ providedIn: 'root' })
export class AssistAuthService {
  // Geeft aan of de authenticatieserver (OpenID-discovery) bereikbaar is. Bij false blijft
  // MijnAssist bruikbaar voor publieke routes, maar worden de aanmeld-knoppen verborgen.
  private _isAuthAvailable = false;

  constructor(private oauthService: OAuthService) { }

  /**
   * Configureert OAuth en laadt het discovery document tijdens de app-bootstrap.
   * Faalt dit (bv. 500 op /.well-known/openid-configuration), dan blokkeert dat de bootstrap
   * niet: isAuthAvailable blijft false en MijnAssist blijft bruikbaar zonder account — enkel de
   * aanmeld-knoppen worden verborgen.
   */
  async initialize(): Promise<void> {
    this.oauthService.configure(authConfig);
    this.oauthService.setStorage(localStorage); // gebruik sessionStorage als je dat liever hebt

    try {
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();
      this._isAuthAvailable = true;
    } catch (err) {
      this._isAuthAvailable = false;
      console.error(
        'OpenID-discovery niet beschikbaar — aanmelden is tijdelijk uitgeschakeld, MijnAssist blijft bruikbaar zonder account.',
        err
      );
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const hasToken = this.oauthService.hasValidAccessToken();

    if (params.get('activated') == '1' && !hasToken) {
      sessionStorage.setItem('returnUrl', window.location.href);
      this.oauthService.initCodeFlow();
      return;
    }

    const returnUrl = sessionStorage.getItem('returnUrl');
    if (hasToken && returnUrl) {
      sessionStorage.removeItem('returnUrl');
      window.location.replace(returnUrl);
    }
  }

  /** Is de authenticatieserver bereikbaar? Bepaalt of aanmelden mogelijk is. */
  get isAuthAvailable(): boolean {
    return this._isAuthAvailable;
  }

  /** Start de login via Authorization Code Flow met PKCE */
  login(returnUrl: string = "", organizationKey?: string): void {
    if (!this._isAuthAvailable) {
      // Auth-server onbereikbaar: aanmelden is niet mogelijk, knoppen horen verborgen te zijn.
      return;
    }
    sessionStorage.setItem('returnUrl', returnUrl ?? window.location.pathname);
    this.oauthService.initCodeFlow('', organizationKey ? { organizationKey } : {});
  }

  /** Logt de gebruiker uit */
  logout(): void {
    this.oauthService.logOut();

    // Redirect naar auth server logout met return URL
    const returnUrl = encodeURIComponent(window.location.origin);
    window.location.replace(
      `${environment.authenticationServer}Account/logout?returnUrl=${returnUrl}`
    );
  }

  /** Synchronous controle: is de gebruiker ingelogd? */
  get isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  /** Observable die emit wanneer de loginstatus verandert */
  get isLoggedIn$(): Observable<boolean> {
    return this.oauthService.events.pipe(
      filter((e) => [
        'token_received',
        'session_terminated',
        'session_error'
      ].includes(e.type)),
      startWith(this.oauthService.hasValidAccessToken()),
      map(() => this.oauthService.hasValidAccessToken())
    );
  }

  /** Geeft het access token (indien beschikbaar) */
  get accessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  /** Geeft claims uit de ID-token (indien aanwezig) */
  get identityClaims(): any {
    return this.oauthService.getIdentityClaims();
  }

  /** ID van de ingelogde gebruiker (subject claim) */
  get userId(): string | undefined {
    const claims = this.identityClaims;
    console.log("claims", claims);
    return claims?.sub;
  }
 
}
